import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { SelectField } from '../../components/ui/SelectField';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { ApiError } from '../../lib/api';
import { JOB_ROLES, NO_JOB_ROLE_LABEL, jobRoleLabel } from '../../lib/jobRoles';
import {
  NO_FILTERS,
  disableConfirmText,
  filterUsers,
  formatLastSignedIn,
  sortUsers,
  stillManagesWarning,
  type UserFilters,
  type UserRow,
  type UserSortKey,
} from '../../lib/usersView';
import { useCurrentUser } from '../auth';
import {
  disableManagedUser,
  enableManagedUser,
  listManagedUsers,
  patchManagedUser,
} from './api';

const JOB_ROLE_OPTIONS = [
  { value: '', label: NO_JOB_ROLE_LABEL },
  ...JOB_ROLES.map((r) => ({ value: r.key, label: r.label })),
];

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join('') || '?'
  );
}

// Settings -> Users (admins only; the API refuses everyone else). Access is
// Admin / Staff and is still the only thing that restricts anything. Job role
// is what someone does and restricts nothing yet.
export function UsersPage() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const [filters, setFilters] = useState<UserFilters>(NO_FILTERS);
  const [sortKey, setSortKey] = useState<UserSortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [editing, setEditing] = useState<UserRow | null>(null);

  const isAdmin = me.role === 'admin';
  const users = useQuery({
    queryKey: ['admin-users'],
    queryFn: listManagedUsers,
    enabled: isAdmin,
  });

  const rows = useMemo(
    () => sortUsers(filterUsers(users.data ?? [], filters), sortKey, sortDir),
    [users.data, filters, sortKey, sortDir],
  );
  const now = Date.now();

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <PageHeader title="Users" backHref="/settings" />
        <p className="text-sm text-slate-600">Only admins can manage users.</p>
      </div>
    );
  }

  function sortBy(key: UserSortKey) {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'lastSignedIn' ? 'desc' : 'asc');
    }
  }
  const arrow = (key: UserSortKey) => (key === sortKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description="Everyone with a Kyck login. Access (Admin or Staff) controls what someone can do. Job role is what they do, and doesn't restrict anything yet."
        backHref="/settings"
      />

      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="w-64"
          placeholder="Search name or email"
          aria-label="Search users"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        <SelectField
          size="sm"
          aria-label="Filter by job role"
          value={filters.jobRole}
          onValueChange={(v) => setFilters({ ...filters, jobRole: v })}
          options={[
            { value: '', label: 'Any job role' },
            ...JOB_ROLES.map((r) => ({ value: r.key, label: r.label })),
            { value: 'none', label: NO_JOB_ROLE_LABEL },
          ]}
        />
        <SelectField
          size="sm"
          aria-label="Filter by access"
          value={filters.access}
          onValueChange={(v) => setFilters({ ...filters, access: v as UserFilters['access'] })}
          options={[
            { value: '', label: 'Any access' },
            { value: 'admin', label: 'Admin' },
            { value: 'staff', label: 'Staff' },
          ]}
        />
        <SelectField
          size="sm"
          aria-label="Filter by status"
          value={filters.status}
          onValueChange={(v) => setFilters({ ...filters, status: v as UserFilters['status'] })}
          options={[
            { value: '', label: 'Any status' },
            { value: 'active', label: 'Active' },
            { value: 'disabled', label: 'Disabled' },
          ]}
        />
      </div>

      {users.isLoading ? (
        <CenteredSpinner />
      ) : users.error ? (
        <p className="text-sm text-rose-700">
          {users.error instanceof ApiError ? users.error.message : 'Could not load users.'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">
                  <button type="button" onClick={() => sortBy('name')}>
                    User{arrow('name')}
                  </button>
                </th>
                <th className="px-3 py-2">
                  <button type="button" onClick={() => sortBy('role')}>
                    Job role{arrow('role')}
                  </button>
                </th>
                <th className="px-3 py-2">Access</th>
                <th className="px-3 py-2">
                  <button type="button" onClick={() => sortBy('lastSignedIn')}>
                    Last signed in{arrow('lastSignedIn')}
                  </button>
                </th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Accounts managed</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const warn = stillManagesWarning(u);
                return (
                  <tr key={u.uid} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          {u.photoURL ? <AvatarImage src={u.photoURL} alt="" /> : null}
                          <AvatarFallback>{initials(u.name)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium text-slate-900">{u.name}</div>
                          <div className="text-xs text-slate-500">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">{jobRoleLabel(u.jobRole)}</td>
                    <td className="px-3 py-2">
                      <Badge variant={u.role === 'admin' ? 'info' : 'neutral'}>
                        {u.role === 'admin' ? 'Admin' : 'Staff'}
                      </Badge>
                    </td>
                    <td
                      className="px-3 py-2 text-slate-600"
                      title={u.lastSignedInAt ? new Date(u.lastSignedInAt).toLocaleString() : undefined}
                    >
                      {formatLastSignedIn(u.lastSignedInAt, now)}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={u.disabledAt ? 'danger' : 'success'}>
                        {u.disabledAt ? 'Disabled' : 'Active'}
                      </Badge>
                      {warn && <div className="mt-1 text-xs text-amber-700">{warn}</div>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {u.accountsManaged > 0 ? (
                        <Link className="text-kyck-700 hover:underline" to="/accounts">
                          {u.accountsManaged}
                        </Link>
                      ) : (
                        0
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => setEditing(u)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                    No users match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditUserDialog
          user={editing}
          isSelf={editing.uid === me.uid}
          onClose={() => setEditing(null)}
          onChanged={() => {
            void qc.invalidateQueries({ queryKey: ['admin-users'] });
            void qc.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      )}
    </div>
  );
}

function errMsg(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Something went wrong. Try again.';
}

function EditUserDialog({
  user,
  isSelf,
  onClose,
  onChanged,
}: {
  user: UserRow;
  isSelf: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [jobRole, setJobRole] = useState(user.jobRole ?? '');
  const [role, setRole] = useState<'admin' | 'staff'>(user.role);
  const [error, setError] = useState<string | null>(null);
  const [confirmDisable, setConfirmDisable] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      const patch: { jobRole?: string | null; role?: 'admin' | 'staff' } = {};
      if ((user.jobRole ?? '') !== jobRole) patch.jobRole = jobRole === '' ? null : jobRole;
      if (user.role !== role) patch.role = role;
      return patchManagedUser(user.uid, patch);
    },
    onSuccess: () => {
      onChanged();
      onClose();
    },
    onError: (e) => setError(errMsg(e)),
  });

  const toggle = useMutation({
    mutationFn: async () => {
      if (user.disabledAt) await enableManagedUser(user.uid);
      else await disableManagedUser(user.uid);
    },
    onSuccess: () => {
      onChanged();
      onClose();
    },
    onError: (e) => {
      setConfirmDisable(false);
      setError(errMsg(e));
    },
  });

  const dirty = (user.jobRole ?? '') !== jobRole || user.role !== role;

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{user.name}</DialogTitle>
            <DialogDescription>{user.email}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Job role</label>
              <SelectField
                aria-label="Job role"
                value={jobRole}
                onValueChange={setJobRole}
                options={JOB_ROLE_OPTIONS}
              />
              <p className="mt-1 text-xs text-slate-500">
                What they do at Kyck. Doesn't restrict anything yet.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Access</label>
              <SelectField
                aria-label="Access"
                value={role}
                onValueChange={(v) => setRole(v as 'admin' | 'staff')}
                disabled={isSelf}
                options={[
                  { value: 'staff', label: 'Staff' },
                  { value: 'admin', label: 'Admin' },
                ]}
              />
              {isSelf && (
                <p className="mt-1 text-xs text-slate-500">
                  You can't change your own access or turn yourself off.
                </p>
              )}
            </div>
            {error && <p className="text-sm text-rose-700">{error}</p>}
          </div>

          <DialogFooter className="sm:justify-between">
            {!isSelf ? (
              <Button
                variant={user.disabledAt ? 'outline' : 'destructive'}
                onClick={() => (user.disabledAt ? toggle.mutate() : setConfirmDisable(true))}
                disabled={toggle.isPending}
              >
                {user.disabledAt ? 'Turn back on' : 'Turn off access'}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDisable} onOpenChange={setConfirmDisable}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Turn off {user.name}?</DialogTitle>
            <DialogDescription>
              {disableConfirmText(user.name, user.accountsManaged)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDisable(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => toggle.mutate()}
              disabled={toggle.isPending}
            >
              Turn off access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
